import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_USER_LAYOUT_DISCIPLINE_KEY,
  RUNTIME_LAYOUT_EVENT,
  TargetKey,
  getRuntimeLayoutSnapshotContentFingerprint,
  type RuntimeLayoutEntry,
  type RuntimeLayoutFontFace,
  type RuntimeLayoutSnapshot,
} from "src/shared/util/src/layout";
import WebsocketClient from "src/shared/util/src/websocket-client";

type UseRuntimeMeetLayoutCatalogOptions = {
  meetId?: string;
  websocketUrl?: string;
  layoutType?: "widget" | "display";
};

type UseRuntimeMeetLayoutCatalogResult = {
  currentThemeName: string | null;
  layoutsByTargetKey: Map<string, RuntimeLayoutEntry[]>;
  fonts: RuntimeLayoutFontFace[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
};

/**
 * Resolves the current runtime theme for a live meet and builds the list of
 * layouts exposed by the live Electron relay, indexed by `targetKey`.
 *
 * @param options - Meet context and optional layout type filter.
 * @returns The current theme name plus the layouts available for that theme.
 */
export function useRuntimeMeetLayoutCatalog(
  options: UseRuntimeMeetLayoutCatalogOptions,
): UseRuntimeMeetLayoutCatalogResult {
  const { meetId, websocketUrl, layoutType } = options;
  const [snapshot, setSnapshot] = useState<RuntimeLayoutSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestRevision, setRequestRevision] = useState(0);
  const retry = useCallback(() => setRequestRevision((value) => value + 1), []);
  const lastContentFingerprintRef = useRef<string | null>(null);

  useEffect(() => {
    setSnapshot(null);
    lastContentFingerprintRef.current = null;
    if (!meetId || !websocketUrl) {
      setIsLoading(false);
      return;
    }

    const wsClient = WebsocketClient.getInstance(websocketUrl);
    setIsLoading(true);
    setError(null);
    let disposed = false;
    const timeout = setTimeout(() => {
      if (!disposed) {
        setError(
          "Les maquettes de cet affichage n’ont pas été reçues. Vérifiez que le poste de compétition est connecté au direct.",
        );
        setIsLoading(false);
      }
    }, 15000);

    const applySnapshot = (nextSnapshot: RuntimeLayoutSnapshot) => {
      clearTimeout(timeout);
      setError(null);
      const fingerprint =
        getRuntimeLayoutSnapshotContentFingerprint(nextSnapshot);
      if (lastContentFingerprintRef.current === fingerprint) {
        setIsLoading(false);
        return;
      }

      lastContentFingerprintRef.current = fingerprint;
      setSnapshot(nextSnapshot);
      setIsLoading(false);
    };

    const handleSnapshot = (payload: unknown) => {
      if (
        !payload ||
        typeof payload !== "object" ||
        !("meetId" in payload) ||
        payload.meetId !== meetId
      ) {
        return;
      }

      applySnapshot(payload as RuntimeLayoutSnapshot);
    };

    const setup = () => {
      if (disposed) {
        return;
      }

      wsClient.off(RUNTIME_LAYOUT_EVENT.SNAPSHOT, handleSnapshot);
      wsClient.on(RUNTIME_LAYOUT_EVENT.SNAPSHOT, handleSnapshot);
      wsClient.emit(RUNTIME_LAYOUT_EVENT.REQUEST_SNAPSHOT, { meetId });
    };

    if (!wsClient.isConnected()) {
      wsClient.connect(setup);
    } else {
      setup();
    }

    return () => {
      disposed = true;
      clearTimeout(timeout);
      wsClient.off(RUNTIME_LAYOUT_EVENT.SNAPSHOT, handleSnapshot);
    };
  }, [meetId, websocketUrl, requestRevision]);

  const layoutsByTargetKey = useMemo(() => {
    const entries = new Map<TargetKey, RuntimeLayoutEntry[]>();
    if (!snapshot) {
      return entries;
    }

    for (const layout of snapshot.layouts) {
      if (layoutType && layout.type !== layoutType) continue;
      const targetKey = layout.targetKey as TargetKey;
      const targetEntries = entries.get(targetKey) ?? [];
      const normalizedLayout = {
        ...layout,
        disciplineKey:
          layout.disciplineKey ?? DEFAULT_USER_LAYOUT_DISCIPLINE_KEY,
      };
      entries.set(targetKey, [...targetEntries, normalizedLayout]);
    }

    return entries;
  }, [layoutType, snapshot]);

  return {
    currentThemeName: snapshot?.themeName ?? null,
    layoutsByTargetKey,
    fonts: snapshot?.fonts ?? [],
    isLoading,
    error,
    retry,
  };
}
